import { forwardRef, Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { CustomerInfoLoadIfNormalUserGuard } from 'app/core/guards/customer-info-load-if-normal-user.guard';
import { DependencyTreeCanActivate } from 'app/core/guards/dependency-tree.guard';
import { GetDefinitionsGuard } from 'app/core/store/definitions/definitions.resolve';
import { AuthorizationGuard } from 'app/core/store/session/authorization/authorization.resolve';
import { interval, Observable, of } from 'rxjs';
import * as fromRoot from '..';
import * as actions from './stock.actions';
import { debounce, filter, first, switchMap } from 'rxjs/operators';

/**
 * Guard to async load the Power BI url before following a route
 */
@Injectable({ providedIn: 'root' })
export class GetPowerBIUrlGuard implements CanActivate, DependencyTreeCanActivate {
  private store: Store<fromRoot.RootState>;

  constructor(@Inject(forwardRef(() => Store)) store) {
    this.store = store;
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.store.pipe(
      first(),
      switchMap((root: fromRoot.RootState) => {
        if (root.stock.powerBIConfig.loaded) {
          return of(true);
        }

        this.store.dispatch(new actions.PowerBIUrlLoadAction());
        return this.store.pipe(
          debounce(root => interval((root.stock.powerBIConfig.loaded && 1) || 200)), // Give ngrx effects the time to start new actions so that things that need to be loaded can start loading
          filter((root: fromRoot.RootState) => !root.stock.powerBIConfig.loading),
          first(),
          switchMap(root => {
            return of(root.stock.powerBIConfig.loaded);
          })
        );
      })
    );
  }

  getDependencies(): any {
    return [AuthorizationGuard, GetDefinitionsGuard, CustomerInfoLoadIfNormalUserGuard];
  }

  getDependencyRouteData(): any {
    return {
      authorization: 'STOCK.GET_CURRENT_STOCK_LIST',
    };
  }
}
